/**
 * HEDEF: EŞİTLENEN YEREL KLASÖR (File System Access API).
 *
 * Kullanıcı bilgisayarındaki bir klasörü seçer — tipik olarak Google Drive
 * for Desktop, Dropbox, iCloud Drive ya da OneDrive'ın eşitlediği bir klasör.
 * Parafon oraya yazar, buluta taşımayı masaüstü istemcisi yapar.
 *
 * NİÇİN BU SEÇENEK VAR: hiçbir uygulama kaydı, OAuth ve üçüncü taraf alan
 * adı gerektirmiyor; CSP'ye dokunmuyor. En az engelli seçenek bu.
 *
 * SINIRLAR (ekranda da yazıyor):
 *  - Yalnızca masaüstü Chrome/Edge (`showDirectoryPicker`). Firefox, Safari
 *    ve mobil tarayıcılarda seçenek görünmez.
 *  - İzin oturumlar arasında kalıcı olmayabilir: tarayıcı yeniden
 *    açıldığında `queryPermission` "prompt" dönerse yedek BEKLER ve tek
 *    tıklık "yeniden bağlan" görünür (Chrome 122+ "her ziyarette izin ver"
 *    seçeneği sunuyor; seçilirse sorun kalmaz).
 *  - Klasör tutamacı IndexedDB'de saklanıyor (yapısal kopyalama); site
 *    verisi silinirse klasör yeniden seçilir.
 */

import { M } from "../../dil";
import type { BulutDosyasi, BulutHedefi, HedefHazirlik, HedefKaydi } from "../tipler";
import { BaglantiGerekliHatasi } from "../yedekleyici";

/** `lib.dom`da olmayan (WICG) izin yüzeyi. */
interface IzinliTutamac {
  queryPermission?(opts: { mode: "readwrite" }): Promise<PermissionState>;
  requestPermission?(opts: { mode: "readwrite" }): Promise<PermissionState>;
}

interface KlasorSecici {
  showDirectoryPicker?(opts?: {
    id?: string;
    mode?: "read" | "readwrite";
    startIn?: string;
  }): Promise<FileSystemDirectoryHandle>;
}

/**
 * Klasör seçici YOKSA sebebi — kullanıcı "neden seçemiyorum" diye sormasın.
 *
 * Brave özelliği Chromium'dan alıyor ama VARSAYILAN KAPALI tutuyor (canlı,
 * 28.09.2026: seçenek "masaüstü Chrome ya da Edge gerekir" diyordu, kullanıcı
 * Brave'deydi — mesaj yanlış yönlendiriyordu). Brave kendini
 * `navigator.brave` ile tanıtıyor.
 */
export function yerelKlasorEngeli(): string | null {
  if (yerelKlasorDestekleniyor()) return null;
  const nav = globalThis.navigator as (Navigator & { brave?: unknown }) | undefined;
  const ua = nav?.userAgent ?? "";
  if (nav?.brave !== undefined) {
    return M.yedek.hedef.klasorBrave;
  }
  if (/Android|iPhone|iPad|Mobile/i.test(ua)) return M.yedek.hedef.klasorMobil;
  if (/Firefox\//.test(ua)) return M.yedek.hedef.klasorFirefox;
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return M.yedek.hedef.klasorSafari;
  return M.yedek.hedef.klasorYok;
}

export function yerelKlasorDestekleniyor(): boolean {
  return typeof (globalThis as unknown as KlasorSecici).showDirectoryPicker === "function";
}

/** Kullanıcı hareketinin içinden çağrılır. */
export async function yerelKlasorSec(): Promise<HedefKaydi> {
  const secici = (globalThis as unknown as KlasorSecici).showDirectoryPicker;
  if (secici === undefined) {
    throw new Error(M.yedek.hedef.klasorSecilemiyor);
  }
  const klasor = await secici.call(globalThis, { id: "parafon-yedek", mode: "readwrite" });
  const izin = await izinIste(klasor);
  if (izin !== "granted") throw new Error(M.yedek.hedef.klasorIzinYok);
  return { tur: "yerel-klasor", klasor, ad: klasor.name };
}

async function izinSor(k: FileSystemDirectoryHandle): Promise<PermissionState> {
  const t = k as unknown as IzinliTutamac;
  // İzin yüzeyi olmayan (eski) uygulamalarda tutamaç elde olduğu sürece yazılabilir.
  return t.queryPermission === undefined ? "granted" : t.queryPermission({ mode: "readwrite" });
}

async function izinIste(k: FileSystemDirectoryHandle): Promise<PermissionState> {
  const t = k as unknown as IzinliTutamac;
  return t.requestPermission === undefined ? "granted" : t.requestPermission({ mode: "readwrite" });
}

function yazmaHatasi(e: unknown): never {
  if (e instanceof DOMException && (e.name === "NotAllowedError" || e.name === "SecurityError")) {
    throw new BaglantiGerekliHatasi(M.yedek.hedef.klasorIzinGeriAlindi);
  }
  if (e instanceof DOMException && e.name === "NotFoundError") {
    throw new Error(M.yedek.hedef.klasorKayboldu);
  }
  throw e;
}

export function yerelKlasorHedefi(
  kayit: Extract<HedefKaydi, { tur: "yerel-klasor" }>,
): BulutHedefi {
  const { klasor } = kayit;
  return {
    tur: "yerel-klasor",
    konum: M.yedek.hedef.klasorKonum(kayit.ad),
    async hazirlik(): Promise<HedefHazirlik> {
      return (await izinSor(klasor)) === "granted" ? "hazir" : "etkilesim-gerekli";
    },
    async yenidenBaglan() {
      if ((await izinIste(klasor)) !== "granted") throw new Error(M.yedek.hedef.klasorIzinYok);
    },
    async yaz(ad, metin) {
      try {
        const dosya = await klasor.getFileHandle(ad, { create: true });
        const yazici = await dosya.createWritable();
        await yazici.write(metin);
        await yazici.close();
      } catch (e: unknown) {
        yazmaHatasi(e);
      }
    },
    async listele() {
      const out: BulutDosyasi[] = [];
      try {
        for await (const [ad, h] of (klasor as unknown as AsyncIterable<[string, FileSystemHandle]>)) {
          if (h.kind !== "file" || !ad.endsWith(".pfai")) continue;
          const f = await (h as FileSystemFileHandle).getFile();
          out.push({ ad, degisti: new Date(f.lastModified).toISOString(), boyut: f.size });
        }
      } catch (e: unknown) {
        yazmaHatasi(e);
      }
      return out;
    },
    async oku(ad) {
      try {
        return await (await (await klasor.getFileHandle(ad)).getFile()).text();
      } catch (e: unknown) {
        yazmaHatasi(e);
      }
    },
    async sil(ad) {
      try {
        await klasor.removeEntry(ad);
      } catch (e: unknown) {
        yazmaHatasi(e);
      }
    },
    kayit: () => kayit,
  };
}
